import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Detects when an element enters the viewport.
 * Once visible, stays visible (one-shot trigger).
 */
export function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/**
 * Returns current window.scrollY, updated on scroll.
 */
export function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const handler = () => setY(window.scrollY);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return y;
}

/**
 * Animates a numeric value from 0 to target when inView becomes true.
 * target format: "200+" | "50+" | "2M+"
 */
export function useCountUp(target, inView, duration = 1600) {
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const numeric = parseInt(target.replace(/\D/g, ""), 10);
    const suffix  = target.replace(/[\d]/g, "");
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.floor(eased * numeric) + suffix);
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(target);
    };

    requestAnimationFrame(step);
  }, [inView, target, duration]);

  return display;
}
